import { useState } from 'react'
import { Head, Link, usePage, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DeleteConfirmModal from '@/components/DeleteConfirmModal'
import CommissionDetailPanel from '@/components/CommissionDetailPanel'
import EquityCreditDetailPanel from '@/components/EquityCreditDetailPanel'
import AgentPasswordActions from '@/components/AgentPasswordActions'
import { 
  Edit, ArrowLeft, Shield, DollarSign, Award, Users, UserMinus, UserCheck, Trash2,
  AlertTriangle, Eye, Copy, Check, KeyRound, X
} from 'lucide-react'

interface Agent {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  onboarding_status: string
  override_percentage: number
  phone: string
  address_line1: string
  address_line2: string | null
  city: string
  state: string
  zip: string
  npn_number: string | null
  carrier_appointments?: Array<{
    id: number
    carrier_id: number
    carrier_name: string | null
    producer_number: string
  }>
  can_send_referrals: boolean
  excluded_from_equity?: boolean
  contract_signed_at: string | null
  activated_at: string | null
  surancebay_completed_at: string | null
  ffr_registration_completed_at: string | null
  parent_agent_id: number | null
  parent_agent_name: string | null
  license_count: number
  sub_agent_count: number
  // Money fields are omitted entirely when the organization hides commissions
  // and equity from below-administrator users, so they are all optional.
  commission_count?: number
  total_commissions_received?: number
  total_override_received?: number
  pending_equity_summary?: {
    pending_count: number
    pending_total: number
    earliest_expires_at: string | null
    // Only present for admin/staff viewers — omitted entirely for non-admin agents
    expired_count?: number
    expired_total?: number
    expired_latest_at?: string | null
  }
  created_at: string
}

interface License {
  id: number
  state_code: string
  license_number: string
  primary_residence_state: boolean
  status: string
  issued_at: string
  expires_at: string
  days_until_expiry: number | null
}

interface Commission {
  id: number
  policy_number: string
  commission_type: string
  carrier_name: string | null
  gross_amount: number
  agent_amount: number
  override_amount: number | null
  status: string
  period_start: string
  period_end: string
  created_at: string
}

interface EquityCredit {
  id: number
  credit_amount: number
  credit_type: string
  event_name: string
  event_date: string
  notes: string | null
  created_at: string
}

interface SubAgent {
  id: number
  full_name: string
  onboarding_status: string
}

interface Props extends PageProps {
  agent: Agent
  licenses: License[]
  commissions?: Commission[]
  equity_credits?: EquityCredit[]
  sub_agents: SubAgent[]
  can_manage_passwords: boolean
  can_manage_surancebay: boolean
  show_financials?: boolean
  sendgrid_connected: boolean
}

type TabType = 'overview' | 'licenses' | 'commissions' | 'equity'

export default function AgentShow({ agent, licenses, commissions, equity_credits, sub_agents, can_manage_passwords, can_manage_surancebay, show_financials = true, sendgrid_connected }: Props) {
  const { auth, flash } = usePage<PageProps>().props
  const canDelete = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [openCommissionId, setOpenCommissionId] = useState<number | null>(null)
  const [openCreditId, setOpenCreditId] = useState<number | null>(null)
  const todayISO = new Date().toISOString().slice(0, 10)
  const [editingSurancebay, setEditingSurancebay] = useState(false)
  const [surancebayDate, setSurancebayDate] = useState(todayISO)
  const [surancebaySubmitting, setSurancebaySubmitting] = useState(false)
  const [editingFfr, setEditingFfr] = useState(false)
  const [ffrDate, setFfrDate] = useState(todayISO)
  const [ffrSubmitting, setFfrSubmitting] = useState(false)

  const submitSurancebay = (payload: { completed_on?: string; clear?: boolean }) => {
    setSurancebaySubmitting(true)
    router.post(`/agents/${agent.id}/update_surancebay`, payload, {
      preserveScroll: true,
      onFinish: () => {
        setSurancebaySubmitting(false)
        setEditingSurancebay(false)
      },
    })
  }

  const submitFfr = (payload: { completed_on?: string; clear?: boolean }) => {
    setFfrSubmitting(true)
    router.post(`/agents/${agent.id}/update_ffr_registration`, payload, {
      preserveScroll: true,
      onFinish: () => {
        setFfrSubmitting(false)
        setEditingFfr(false)
      },
    })
  }

  const initialPasswordBanner =
    flash?.agent_initial_password && flash.agent_initial_password.agent_id === agent.id
      ? flash.agent_initial_password
      : null
  const [showInitialPasswordBanner, setShowInitialPasswordBanner] = useState(!!initialPasswordBanner)
  const [copiedPassword, setCopiedPassword] = useState(false)
  const copyInitialPassword = async () => {
    if (!initialPasswordBanner) return
    try {
      await navigator.clipboard.writeText(initialPasswordBanner.password)
      setCopiedPassword(true)
      setTimeout(() => setCopiedPassword(false), 2000)
    } catch {
      // clipboard blocked; user can still read & copy manually
    }
  }

  const openCommissionPanel = (id: number) => {
    setOpenCreditId(null)
    setOpenCommissionId(id)
  }
  const openCreditPanel = (id: number) => {
    setOpenCommissionId(null)
    setOpenCreditId(id)
  }
  const refreshAgentData = () => {
    router.reload({ only: ['agent', 'commissions', 'equity_credits'] })
  }
  
  const statusColors: Record<string, string> = {
    prospect: 'bg-gray-100 text-gray-700',
    invited: 'bg-blue-100 text-blue-700',
    contract_pending: 'bg-yellow-100 text-yellow-700',
    pending_contract: 'bg-yellow-100 text-yellow-700',
    documents_pending: 'bg-orange-100 text-orange-700',
    training_pending: 'bg-purple-100 text-purple-700',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-red-100 text-red-700'
  }

  const commissionStatusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700'
  }

  const creditTypeColors: Record<string, string> = {
    production: 'bg-green-100 text-green-700',
    override: 'bg-blue-100 text-blue-700',
    bonus: 'bg-purple-100 text-purple-700',
    adjustment: 'bg-orange-100 text-orange-700'
  }

  // Absent when the organization hides commissions and equity — the tabs and
  // stat rows below drop out entirely rather than rendering empty or as $0.00.
  const commissionRows = commissions ?? []
  const equityRows = equity_credits ?? []
  const showFinancials = show_financials && Boolean(commissions && equity_credits)

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: Users },
    { id: 'licenses' as TabType, label: `Licenses (${licenses.length})`, icon: Shield },
    ...(showFinancials
      ? [
          { id: 'commissions' as TabType, label: `Commissions (${commissionRows.length})`, icon: DollarSign },
          { id: 'equity' as TabType, label: `Equity (${equityRows.length})`, icon: Award },
        ]
      : []),
  ]

  const totalEquity = equityRows.reduce((sum, c) => sum + c.credit_amount, 0)
  const totalCommissions = commissionRows.reduce((sum, c) => sum + c.agent_amount, 0)

  return (
    <AppLayout>
      <Head title={`Agent: ${agent.full_name}`} />

      <div className="space-y-6">
        {initialPasswordBanner && showInitialPasswordBanner && (
          <div className="rounded-lg border border-chestnut-forest/30 bg-chestnut-forest/5 p-4">
            <div className="flex items-start gap-3">
              <KeyRound className="h-5 w-5 text-chestnut-forest mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-chestnut-charcoal">
                  Initial login password for {initialPasswordBanner.agent_name}
                </p>
                <p className="text-sm text-chestnut-charcoal/70 mt-0.5">
                  Shown once — copy it now and share it with the agent. They can change it any time from the login screen.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="px-3 py-2 rounded bg-white border border-chestnut-charcoal/10 font-mono text-sm break-all">
                    {initialPasswordBanner.email}
                  </div>
                  <div className="px-3 py-2 rounded bg-white border border-chestnut-charcoal/10 font-mono text-sm break-all">
                    {initialPasswordBanner.password}
                  </div>
                  <button
                    type="button"
                    onClick={copyInitialPassword}
                    className="btn-secondary flex items-center gap-2"
                  >
                    {copiedPassword ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copiedPassword ? 'Copied' : 'Copy password'}
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowInitialPasswordBanner(false)}
                className="p-1 text-chestnut-charcoal/50 hover:text-chestnut-charcoal"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-4">
          <Link href="/agents" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
              {agent.full_name}
            </h1>
            <p className="text-chestnut-charcoal/70 mt-1">
              {agent.email} | <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[agent.onboarding_status]}`}>
                {agent.onboarding_status.replace(/_/g, ' ')}
              </span>
              {agent.excluded_from_equity && (
                <span
                  className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800"
                  title="This agent does not accrue equity credits on any commission (writing or override)."
                >
                  Excluded from equity
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {can_manage_passwords && (
              <AgentPasswordActions
                agentId={agent.id}
                agentName={agent.full_name}
                sendgridConnected={sendgrid_connected}
                variant="buttons"
              />
            )}
            {canDelete && (
              <button
                onClick={() => router.post(`/admin/impersonate/${agent.id}`)}
                className="btn-secondary flex items-center gap-2"
                title="View as this agent"
              >
                <Eye className="h-4 w-4" />
                Impersonate
              </button>
            )}
            {agent.onboarding_status === 'active' ? (
              /* Deactivating switches the account off, so the button spells out
                 what the admin cannot see from here: the person loses access. */
              <button
                onClick={() => {
                  if (window.confirm(
                    `Deactivate ${agent.full_name}?\n\n` +
                    'This switches their account off. They will be signed out on their next action ' +
                    'and will not be able to sign back in until you reactivate the account.'
                  )) {
                    router.post(`/agents/${agent.id}/deactivate`)
                  }
                }}
                className="btn-secondary flex items-center gap-2"
                title="Switches the account off: signs them out and blocks sign-in until reactivated"
              >
                <UserMinus className="h-4 w-4" />
                Deactivate
              </button>
            ) : agent.onboarding_status === 'inactive' ? (
              <Link
                href={`/agents/${agent.id}/reactivate`}
                method="post"
                as="button"
                className="btn-secondary flex items-center gap-2"
                title="Switches the account back on so they can sign in again"
              >
                <UserCheck className="h-4 w-4" />
                Reactivate
              </Link>
            ) : (
              <Link
                href={`/agents/${agent.id}/activate`}
                method="post"
                as="button"
                className="btn-secondary flex items-center gap-2"
                title="Marks the agent active and lets them sign in"
              >
                <UserCheck className="h-4 w-4" />
                Activate Agent
              </Link>
            )}
            <Link href={`/agents/${agent.id}/edit`} className="btn-primary flex items-center gap-2">
              <Edit className="h-4 w-4" />
              Edit
            </Link>
            {canDelete && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-600 text-red-600 font-medium hover:bg-red-600 hover:text-white transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Permanently delete
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-chestnut-charcoal/10">
          <nav className="flex gap-6 -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal hover:border-chestnut-charcoal/20'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="card">
                <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">Agent Details</h2>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-chestnut-charcoal/60">Phone</dt>
                    <dd className="font-medium">{agent.phone || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-chestnut-charcoal/60">NPN Number</dt>
                    <dd className="font-medium">{agent.npn_number || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-chestnut-charcoal/60">Override %</dt>
                    <dd className="font-medium">{agent.override_percentage}%</dd>
                  </div>
                  {agent.parent_agent_name && (
                    <div>
                      <dt className="text-sm text-chestnut-charcoal/60">Sponsoring Agent</dt>
                      <dd className="font-medium">
                        <Link href={`/agents/${agent.parent_agent_id}`} className="text-chestnut-forest hover:underline">
                          {agent.parent_agent_name}
                        </Link>
                      </dd>
                    </div>
                  )}
                  <div className="col-span-2">
                    <dt className="text-sm text-chestnut-charcoal/60">Address</dt>
                    <dd className="font-medium">
                      {agent.address_line1 ? (
                        <>{agent.address_line1}{agent.address_line2 && `, ${agent.address_line2}`}<br />{agent.city}, {agent.state} {agent.zip}</>
                      ) : '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-chestnut-charcoal/60">Contract Signed</dt>
                    <dd className="font-medium">{agent.contract_signed_at ? new Date(agent.contract_signed_at).toLocaleDateString() : '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-chestnut-charcoal/60">Activated</dt>
                    <dd className="font-medium">{agent.activated_at ? new Date(agent.activated_at).toLocaleDateString() : '-'}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm text-chestnut-charcoal/60">SuranceBay Profile</dt>
                    <dd className="font-medium">
                      <div className="flex flex-wrap items-center gap-3">
                        {agent.surancebay_completed_at ? (
                          <span className="inline-flex items-center gap-1.5 text-chestnut-forest">
                            <Check className="w-4 h-4" />
                            Completed {new Date(agent.surancebay_completed_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-chestnut-charcoal/60">Not completed</span>
                        )}
                        {can_manage_surancebay && !editingSurancebay && (
                          agent.surancebay_completed_at ? (
                            <button
                              type="button"
                              onClick={() => submitSurancebay({ clear: true })}
                              disabled={surancebaySubmitting}
                              className="text-sm text-chestnut-burgundy hover:underline disabled:opacity-50"
                            >
                              Clear completion
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => { setSurancebayDate(todayISO); setEditingSurancebay(true) }}
                              className="text-sm text-chestnut-forest hover:underline"
                            >
                              Mark complete
                            </button>
                          )
                        )}
                      </div>
                      {can_manage_surancebay && editingSurancebay && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <input
                            type="date"
                            value={surancebayDate}
                            max={todayISO}
                            onChange={e => setSurancebayDate(e.target.value)}
                            className="input-field w-auto text-sm py-1.5"
                          />
                          <button
                            type="button"
                            onClick={() => submitSurancebay({ completed_on: surancebayDate })}
                            disabled={surancebaySubmitting || !surancebayDate}
                            className="btn-primary text-sm py-1.5 px-3 disabled:opacity-50"
                          >
                            {surancebaySubmitting ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingSurancebay(false)}
                            disabled={surancebaySubmitting}
                            className="btn-secondary text-sm py-1.5 px-3"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm text-chestnut-charcoal/60">FFR Registration</dt>
                    <dd className="font-medium">
                      <div className="flex flex-wrap items-center gap-3">
                        {agent.ffr_registration_completed_at ? (
                          <span className="inline-flex items-center gap-1.5 text-chestnut-forest">
                            <Check className="w-4 h-4" />
                            Completed {new Date(agent.ffr_registration_completed_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-chestnut-charcoal/60">Not completed</span>
                        )}
                        {can_manage_surancebay && !editingFfr && (
                          agent.ffr_registration_completed_at ? (
                            <button
                              type="button"
                              onClick={() => submitFfr({ clear: true })}
                              disabled={ffrSubmitting}
                              className="text-sm text-chestnut-burgundy hover:underline disabled:opacity-50"
                            >
                              Clear completion
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => { setFfrDate(todayISO); setEditingFfr(true) }}
                              className="text-sm text-chestnut-forest hover:underline"
                            >
                              Mark complete
                            </button>
                          )
                        )}
                      </div>
                      {can_manage_surancebay && editingFfr && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <input
                            type="date"
                            value={ffrDate}
                            max={todayISO}
                            onChange={e => setFfrDate(e.target.value)}
                            className="input-field w-auto text-sm py-1.5"
                          />
                          <button
                            type="button"
                            onClick={() => submitFfr({ completed_on: ffrDate })}
                            disabled={ffrSubmitting || !ffrDate}
                            className="btn-primary text-sm py-1.5 px-3 disabled:opacity-50"
                          >
                            {ffrSubmitting ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingFfr(false)}
                            disabled={ffrSubmitting}
                            className="btn-secondary text-sm py-1.5 px-3"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">Carrier Appointments</h2>
                  <Link href={`/agents/${agent.id}/edit`} className="text-sm text-chestnut-forest hover:underline">
                    Manage
                  </Link>
                </div>
                {(agent.carrier_appointments?.length ?? 0) === 0 ? (
                  <p className="text-sm text-chestnut-charcoal/60">No carrier appointments on file.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b border-chestnut-charcoal/10">
                        <tr className="text-left text-sm text-chestnut-charcoal/60">
                          <th className="pb-2 font-medium">Carrier</th>
                          <th className="pb-2 font-medium">Writing / Producer Number</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-chestnut-charcoal/5">
                        {agent.carrier_appointments!.map(ca => (
                          <tr key={ca.id}>
                            <td className="py-2">{ca.carrier_name || '—'}</td>
                            <td className="py-2 font-mono text-sm">{ca.producer_number}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {canDelete && (
                <div className="card">
                  <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">Permissions</h2>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium text-chestnut-charcoal">Can send referrals</div>
                      <p className="text-sm text-chestnut-charcoal/60">
                        When off, this agent cannot invite or resend agreements to new referrals. Existing referrals are unaffected.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={agent.can_send_referrals}
                      onClick={() => {
                        router.patch(
                          `/agents/${agent.id}`,
                          { agent: { can_send_referrals: !agent.can_send_referrals } },
                          { preserveScroll: true, only: ['agent'] }
                        )
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        agent.can_send_referrals ? 'bg-chestnut-forest' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          agent.can_send_referrals ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}

              {!agent.contract_signed_at && agent.pending_equity_summary && (agent.pending_equity_summary.pending_count > 0 || (agent.pending_equity_summary.expired_count ?? 0) > 0) && (
                <div className="card border-l-4 border-l-orange-400">
                  <div className="flex items-start gap-3 mb-3">
                    <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                    <div>
                      <h2 className="text-lg font-semibold text-chestnut-charcoal font-heading">Pending Equity Credits</h2>
                      <p className="text-sm text-chestnut-charcoal/60">
                        Equity credits are held until this agent signs their contract.
                      </p>
                    </div>
                  </div>

                  {/* Live pending credits */}
                  {agent.pending_equity_summary.pending_count > 0 && (
                    <dl className="grid grid-cols-2 gap-4">
                      <div>
                        <dt
                          className="text-sm text-chestnut-charcoal/60"
                          title="Number of pending commission events and the dollar total of equity credits being held until the agent signs their contract."
                        >
                          Pending Commissions (Credits $)
                        </dt>
                        <dd
                          className="font-medium"
                          title={`${agent.pending_equity_summary.pending_count} pending commission${agent.pending_equity_summary.pending_count === 1 ? '' : 's'} totaling $${agent.pending_equity_summary.pending_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in equity credits, pending contract signature.`}
                        >
                          {agent.pending_equity_summary.pending_count} pending commission{agent.pending_equity_summary.pending_count === 1 ? '' : 's'}
                          {' '}({agent.pending_equity_summary.pending_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in credits)
                        </dd>
                      </div>
                      {agent.pending_equity_summary.earliest_expires_at && (() => {
                        const earliest = new Date(agent.pending_equity_summary.earliest_expires_at!)
                        const days = Math.max(0, Math.ceil((earliest.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
                        return (
                          <div>
                            <dt className="text-sm text-chestnut-charcoal/60">Earliest Expiration</dt>
                            <dd className="font-medium text-orange-700">
                              {days} day{days === 1 ? '' : 's'} ({earliest.toLocaleDateString()})
                            </dd>
                          </div>
                        )
                      })()}
                    </dl>
                  )}

                  {/* Expired / forfeited credits — admin-only fields; only rendered when
                      the server included them (viewer_is_admin: true on the backend) */}
                  {(agent.pending_equity_summary.expired_count ?? 0) > 0 && (
                    <div className={agent.pending_equity_summary.pending_count > 0 ? 'mt-4 pt-4 border-t border-chestnut-charcoal/10' : ''}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2">Forfeited (contract not signed in time)</p>
                      <dl className="grid grid-cols-2 gap-4">
                        <div>
                          <dt
                            className="text-sm text-chestnut-charcoal/60"
                            title="Number of commission events whose equity credits were forfeited because the contract wasn't signed before the deadline."
                          >
                            Forfeited Commissions (Credits $)
                          </dt>
                          <dd
                            className="font-medium text-red-600"
                            title={`${agent.pending_equity_summary.expired_count} forfeited commission${agent.pending_equity_summary.expired_count === 1 ? '' : 's'} totaling $${(agent.pending_equity_summary.expired_total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in equity credits lost to expiration.`}
                          >
                            {agent.pending_equity_summary.expired_count} forfeited commission{agent.pending_equity_summary.expired_count === 1 ? '' : 's'}
                            {' '}({(agent.pending_equity_summary.expired_total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in credits)
                          </dd>
                        </div>
                        {agent.pending_equity_summary.expired_latest_at && (
                          <div>
                            <dt className="text-sm text-chestnut-charcoal/60">Last Expired On</dt>
                            <dd className="font-medium text-red-600">
                              {new Date(agent.pending_equity_summary.expired_latest_at).toLocaleDateString()}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}
                </div>
              )}

              {sub_agents.length > 0 && (
                <div className="card">
                  <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">Sponsored Agents ({sub_agents.length})</h2>
                  <div className="space-y-2">
                    {sub_agents.map((subAgent) => (
                      <Link key={subAgent.id} href={`/agents/${subAgent.id}`} className="flex items-center justify-between p-3 bg-chestnut-cream rounded-lg hover:bg-chestnut-gray/50">
                        <span className="font-medium">{subAgent.full_name}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[subAgent.onboarding_status]}`}>
                          {subAgent.onboarding_status.replace(/_/g, ' ')}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="card">
                <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">Quick Stats</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-chestnut-forest/10 rounded-lg"><Shield className="h-5 w-5 text-chestnut-forest" /></div>
                    <div><div className="text-2xl font-bold">{licenses.filter(l => l.status === 'active').length}</div><div className="text-sm text-chestnut-charcoal/60">Active Licenses</div></div>
                  </div>
                  {showFinancials && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-chestnut-gold/10 rounded-lg"><DollarSign className="h-5 w-5 text-chestnut-gold" /></div>
                      <div>
                        <div className="text-2xl font-bold">${totalCommissions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        <div className="text-sm text-chestnut-charcoal/60">Total Commissions ({agent.commission_count || 0} records)</div>
                      </div>
                    </div>
                  )}
                  {showFinancials && (agent.total_override_received || 0) > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg"><DollarSign className="h-5 w-5 text-amber-600" /></div>
                      <div>
                        <div className="text-2xl font-bold">${(agent.total_override_received || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        <div className="text-sm text-chestnut-charcoal/60">Override Earnings (from sponsored agents)</div>
                      </div>
                    </div>
                  )}
                  {showFinancials && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg"><Award className="h-5 w-5 text-purple-600" /></div>
                      <div><div className="text-2xl font-bold">{totalEquity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div><div className="text-sm text-chestnut-charcoal/60">Equity Credits</div></div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-chestnut-gold/10 rounded-lg"><Users className="h-5 w-5 text-chestnut-gold" /></div>
                    <div><div className="text-2xl font-bold">{sub_agents.length}</div><div className="text-sm text-chestnut-charcoal/60">Sponsored Agents</div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'licenses' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">Licenses</h2>
              <Link href={`/agents/${agent.id}/licenses/new`} className="btn-primary text-sm">+ Add License</Link>
            </div>
            {licenses.length === 0 ? (
              <p className="text-chestnut-charcoal/60 py-8 text-center">No licenses on file.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-chestnut-charcoal/10">
                    <tr className="text-left text-sm text-chestnut-charcoal/60">
                      <th className="pb-3 font-medium">State</th>
                      <th className="pb-3 font-medium">License Number</th>
                      <th className="pb-3 font-medium">Residence</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Issued</th>
                      <th className="pb-3 font-medium">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-chestnut-charcoal/5">
                    {licenses.map((license) => (
                      <tr key={license.id} className="hover:bg-chestnut-cream/50 cursor-pointer" onClick={() => router.visit(`/licenses/${license.id}`)}>
                        <td className="py-3 font-medium">{license.state_code}</td>
                        <td className="py-3 font-mono text-sm">{license.license_number}</td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${license.primary_residence_state ? 'bg-chestnut-forest/10 text-chestnut-forest' : 'bg-gray-100 text-gray-600'}`}>
                            {license.primary_residence_state ? 'Primary' : 'Non-Primary'}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${license.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {license.status}
                          </span>
                        </td>
                        <td className="py-3 text-sm">{license.issued_at ? new Date(license.issued_at).toLocaleDateString() : '-'}</td>
                        <td className="py-3">
                          <div className="text-sm">{license.expires_at ? new Date(license.expires_at).toLocaleDateString() : '-'}</div>
                          {license.days_until_expiry !== null && license.days_until_expiry <= 30 && (
                            <div className="text-xs text-orange-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{license.days_until_expiry} days</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'commissions' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">Commissions</h2>
              <div className="text-sm text-chestnut-charcoal/60">Total: <span className="font-semibold text-chestnut-forest">${totalCommissions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            </div>
            {commissionRows.length === 0 ? (
              <p className="text-chestnut-charcoal/60 py-8 text-center">No commissions recorded.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-chestnut-charcoal/10">
                    <tr className="text-left text-sm text-chestnut-charcoal/60">
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Policy #</th>
                      <th className="pb-3 font-medium">Carrier</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium text-right">Gross</th>
                      <th className="pb-3 font-medium text-right">Agent Amount</th>
                      <th className="pb-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-chestnut-charcoal/5">
                    {commissionRows.map((commission) => (
                      <tr key={commission.id} className={`hover:bg-chestnut-cream/50 cursor-pointer ${openCommissionId === commission.id ? 'bg-chestnut-cream' : ''}`} onClick={() => openCommissionPanel(commission.id)}>
                        <td className="py-3 text-sm">{new Date(commission.created_at).toLocaleDateString()}</td>
                        <td className="py-3 font-mono text-sm">{commission.policy_number}</td>
                        <td className="py-3">{commission.carrier_name || '-'}</td>
                        <td className="py-3 capitalize text-sm">{commission.commission_type.replace(/_/g, ' ')}</td>
                        <td className="py-3 text-right text-sm">${commission.gross_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-3 text-right font-semibold text-chestnut-forest">${commission.agent_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${commissionStatusColors[commission.status]}`}>
                            {commission.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'equity' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">Equity Credits</h2>
              <div className="text-sm text-chestnut-charcoal/60">Total: <span className="font-semibold text-chestnut-forest">+{totalEquity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} credits</span></div>
            </div>
            {equityRows.length === 0 ? (
              <p className="text-chestnut-charcoal/60 py-8 text-center">No equity credits earned.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-chestnut-charcoal/10">
                    <tr className="text-left text-sm text-chestnut-charcoal/60">
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Event</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium text-right">Credits</th>
                      <th className="pb-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-chestnut-charcoal/5">
                    {equityRows.map((credit) => (
                      <tr key={credit.id} className={`hover:bg-chestnut-cream/50 cursor-pointer ${openCreditId === credit.id ? 'bg-chestnut-cream' : ''}`} onClick={() => openCreditPanel(credit.id)}>
                        <td className="py-3 text-sm">{new Date(credit.event_date).toLocaleDateString()}</td>
                        <td className="py-3 font-medium">{credit.event_name}</td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${creditTypeColors[credit.credit_type]}`}>
                            {credit.credit_type}
                          </span>
                        </td>
                        <td className="py-3 text-right font-semibold text-chestnut-forest">+{credit.credit_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-3 text-sm text-chestnut-charcoal/60 max-w-xs truncate">{credit.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

      <CommissionDetailPanel
        commissionId={openCommissionId}
        isOpen={openCommissionId !== null}
        onClose={() => setOpenCommissionId(null)}
        onSaved={refreshAgentData}
      />

      <EquityCreditDetailPanel
        creditId={openCreditId}
        isOpen={openCreditId !== null}
        onClose={() => setOpenCreditId(null)}
        onSaved={refreshAgentData}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Permanently delete agent"
        message={
          `This permanently removes the agent and their user account, along with all associated data (licenses, commissions, equity credits, onboarding documents, and carrier appointments). The email ${agent.email} will be freed for reuse.` +
          (agent.sub_agent_count > 0
            ? ` This agent has ${agent.sub_agent_count} downline ${agent.sub_agent_count === 1 ? 'sub-agent' : 'sub-agents'} — they will NOT be deleted but will become top-level agents.`
            : '')
        }
        deleteUrl={`/agents/${agent.id}`}
        itemName={agent.full_name}
        redirectUrl="/agents"
      />
    </AppLayout>
  )
}
